// src/pages/admin/AdminPlansManagement.js
// Admin : gestion des packs d'abonnement (CRUD) — multivendeur
// Maxula

import React, { useState, useEffect, useCallback } from 'react';
import { FiPlus, FiEdit, FiTrash2, FiStar } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { planAPI } from '../../services/api';

const EMPTY = {
  name: '',
  description: '',
  targetType: 'all',
  isFree: false,
  priceMonthly: 0,
  priceQuarterly: 0,
  priceSemiannual: 0,
  priceYearly: 0,
  maxDeals: 5,
  commissionRate: 15,
  featuredDeals: 0,
  maxPhotos: 5,
  tier: 'none',
  features: '',
  color: '#2563eb',
  isPopular: false,
  sortOrder: 0,
  isActive: true
};

const TYPE_LABELS = { all: 'Tous', merchant: 'Marchand', promoteur: 'Promoteur', agence: 'Agence' };

const AdminPlansManagement = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [yearlyEdited, setYearlyEdited] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await planAPI.getAllAdmin();
      setPlans(res.data?.data || []);
    } catch (e) {
      toast.error('Erreur de chargement des packs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setYearlyEdited(false);
    setShowModal(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setYearlyEdited(true);
    setForm({
      ...EMPTY,
      ...p,
      features: Array.isArray(p.features) ? p.features.join('\n') : ''
    });
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'priceYearly') setYearlyEdited(true);
    setForm((f) => {
      const next = { ...f, [name]: type === 'checkbox' ? checked : value };
      // Prix annuel auto-calculé (2 mois offerts) tant que l'admin ne l'a pas modifié manuellement
      if (name === 'priceMonthly' && !yearlyEdited) {
        const m = Number(value) || 0;
        next.priceYearly = m > 0 ? m * 10 : 0;
      }
      return next;
    });
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error('Le nom est requis');
    try {
      setSaving(true);
      const payload = {
        ...form,
        priceMonthly: Number(form.priceMonthly) || 0,
        priceQuarterly: Number(form.priceQuarterly) || 0,
        priceSemiannual: Number(form.priceSemiannual) || 0,
        priceYearly: Number(form.priceYearly) || 0,
        maxDeals: parseInt(form.maxDeals, 10),
        commissionRate: Number(form.commissionRate) || 0,
        featuredDeals: parseInt(form.featuredDeals, 10),
        maxPhotos: parseInt(form.maxPhotos, 10),
        tier: form.tier,
        sortOrder: parseInt(form.sortOrder, 10) || 0,
        features: String(form.features).split('\n').map((s) => s.trim()).filter(Boolean)
      };
      if (editing) {
        await planAPI.update(editing._id, payload);
        toast.success('Pack mis à jour');
      } else {
        await planAPI.create(payload);
        toast.success('Pack créé');
      }
      setShowModal(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer le pack "${p.name}" ?`)) return;
    try {
      await planAPI.remove(p._id);
      toast.success('Pack supprimé');
      load();
    } catch (e) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const limitTxt = (n) => (n === -1 ? '∞' : n);

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Packs d'abonnement</h1>
          <p className="text-gray-500">Définissez les prix et conditions des packs vendeurs.</p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <FiPlus /> Nouveau pack
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
        </div>
      ) : (
        <div className="overflow-x-auto bg-white rounded-xl border border-gray-200">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-4 py-3 text-left">Pack</th>
                <th className="px-4 py-3 text-left">Type</th>
                <th className="px-4 py-3 text-right">Mensuel</th>
                <th className="px-4 py-3 text-right">Annuel</th>
                <th className="px-4 py-3 text-center">Deals</th>
                <th className="px-4 py-3 text-center">Commission</th>
                <th className="px-4 py-3 text-center">En avant</th>
                <th className="px-4 py-3 text-center">Actif</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {plans.map((p) => (
                <tr key={p._id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">
                    <span className="inline-flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full" style={{ background: p.color }} />
                      {p.name}
                      {p.isPopular && <FiStar className="text-yellow-500" size={14} />}
                      {p.isFree && <span className="text-xs text-gray-400">(gratuit)</span>}
                    </span>
                  </td>
                  <td className="px-4 py-3">{TYPE_LABELS[p.targetType] || p.targetType}</td>
                  <td className="px-4 py-3 text-right">{p.isFree ? '—' : `${p.priceMonthly} MAD`}</td>
                  <td className="px-4 py-3 text-right">{p.isFree ? '—' : `${p.priceYearly} MAD`}</td>
                  <td className="px-4 py-3 text-center">{limitTxt(p.maxDeals)}</td>
                  <td className="px-4 py-3 text-center">{p.commissionRate}%</td>
                  <td className="px-4 py-3 text-center">{limitTxt(p.featuredDeals)}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${p.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {p.isActive ? 'Oui' : 'Non'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => openEdit(p)} className="p-2 text-blue-600 hover:bg-blue-50 rounded">
                      <FiEdit />
                    </button>
                    <button onClick={() => remove(p)} className="p-2 text-red-600 hover:bg-red-50 rounded">
                      <FiTrash2 />
                    </button>
                  </td>
                </tr>
              ))}
              {plans.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center text-gray-400">
                    Aucun pack. Cliquez sur « Nouveau pack » ou lancez le script de seed.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* MODAL */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-xl font-bold mb-4">{editing ? 'Modifier le pack' : 'Nouveau pack'}</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">Nom *</label>
                <input name="name" value={form.name} onChange={handleChange}
                  className="w-full border rounded-lg px-3 py-2" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea name="description" value={form.description} onChange={handleChange} rows={2}
                  className="w-full border rounded-lg px-3 py-2" />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Type ciblé</label>
                <select name="targetType" value={form.targetType} onChange={handleChange}
                  className="w-full border rounded-lg px-3 py-2">
                  {Object.keys(TYPE_LABELS).map((t) => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Couleur</label>
                <input type="color" name="color" value={form.color} onChange={handleChange}
                  className="w-full border rounded-lg h-[42px] px-1 py-1" />
              </div>

              <div className="flex items-center gap-2">
                <input type="checkbox" id="isFree" name="isFree" checked={form.isFree} onChange={handleChange} />
                <label htmlFor="isFree" className="text-sm">Pack gratuit</label>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="isPopular" name="isPopular" checked={form.isPopular} onChange={handleChange} />
                <label htmlFor="isPopular" className="text-sm">Marquer « Populaire »</label>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Prix mensuel (MAD)</label>
                <input type="number" name="priceMonthly" value={form.priceMonthly} onChange={handleChange}
                  disabled={form.isFree} className="w-full border rounded-lg px-3 py-2 disabled:bg-gray-100" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Prix annuel (MAD)</label>
                <input type="number" name="priceYearly" value={form.priceYearly} onChange={handleChange}
                  disabled={form.isFree} className="w-full border rounded-lg px-3 py-2 disabled:bg-gray-100" />
                <p className="text-xs text-gray-400 mt-1">Calculé auto (×10 = 2 mois offerts) — modifiable</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Prix trimestriel (MAD)</label>
                <input type="number" name="priceQuarterly" value={form.priceQuarterly} onChange={handleChange}
                  disabled={form.isFree} className="w-full border rounded-lg px-3 py-2 disabled:bg-gray-100" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Prix semestriel (MAD)</label>
                <input type="number" name="priceSemiannual" value={form.priceSemiannual} onChange={handleChange}
                  disabled={form.isFree} className="w-full border rounded-lg px-3 py-2 disabled:bg-gray-100" />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Nb deals max (-1 = illimité)</label>
                <input type="number" name="maxDeals" value={form.maxDeals} onChange={handleChange}
                  className="w-full border rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Commission (%)</label>
                <input type="number" name="commissionRate" value={form.commissionRate} onChange={handleChange}
                  className="w-full border rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Deals en avant (-1 = illimité)</label>
                <input type="number" name="featuredDeals" value={form.featuredDeals} onChange={handleChange}
                  className="w-full border rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Photos max / annonce (-1 = illimité)</label>
                <input type="number" name="maxPhotos" value={form.maxPhotos} onChange={handleChange}
                  className="w-full border rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Graduation (badge marchand)</label>
                <select name="tier" value={form.tier} onChange={handleChange}
                  className="w-full border rounded-lg px-3 py-2">
                  <option value="none">Aucune</option>
                  <option value="bronze">Bronze</option>
                  <option value="silver">Argent</option>
                  <option value="gold">Or</option>
                  <option value="platinum">Platine</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Ordre d'affichage</label>
                <input type="number" name="sortOrder" value={form.sortOrder} onChange={handleChange}
                  className="w-full border rounded-lg px-3 py-2" />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">Avantages (1 par ligne)</label>
                <textarea name="features" value={form.features} onChange={handleChange} rows={4}
                  placeholder={'Statistiques avancées\nSupport prioritaire'}
                  className="w-full border rounded-lg px-3 py-2" />
              </div>

              <div className="flex items-center gap-2 md:col-span-2">
                <input type="checkbox" id="isActive" name="isActive" checked={form.isActive} onChange={handleChange} />
                <label htmlFor="isActive" className="text-sm">Pack actif (visible par les vendeurs)</label>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setShowModal(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">
                Annuler
              </button>
              <button onClick={submit} disabled={saving}
                className="px-4 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60">
                {saving ? 'Enregistrement…' : editing ? 'Mettre à jour' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPlansManagement;
